import { useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UploadSimple, DownloadSimple, CheckCircle, WarningCircle } from "@phosphor-icons/react";

const TABS = [
  {
    key: "cars",
    title: "Import Stok Mobil",
    desc: "Upload data mobil (stok) dari Excel. Kolom: account_number (no. rekening — melekat pada mobil, harus unik), brand (kendaraan), model, type, year (tahun), plate_number (no. polisi), color (warna), price (harga)",
  },
  {
    key: "sales",
    title: "Import Data Penjualan",
    desc: "Barang laku / transaksi. Pelanggan & mobil dibuat otomatis jika belum ada. Kolom: account_number, customer_name, customer_phone, brand, model, plate_number, sale_type (cash/credit), sale_date, total_price, down_payment, tenor_months, payment_period_months, annual_rate",
  },
  {
    key: "dp",
    title: "Import DP Dibayar",
    desc: "Import DP (uang muka) yang sudah dibayarkan. Match berdasarkan no_rekening + plate_number (validasi ganda). Menimpa nilai down_payment pada penjualan yang cocok & mencatat riwayat pembayaran. Kolom: account_number, plate_number, payment_date, amount, notes",
  },
  {
    key: "payments",
    title: "Import Cicilan Dibayar",
    desc: "Riwayat pembayaran cicilan. Match berdasarkan no_rekening + plate_number (validasi ganda). Isi kolom sequence untuk mencatat pembayaran per angsuran; kosongkan sequence untuk pembayaran bulk yang otomatis dibagi ke angsuran paling awal yang belum lunas. Kolom: account_number, plate_number, sequence, payment_date, amount, notes",
  },
];

export default function Import() {
  const [active, setActive] = useState("cars");
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const activeCfg = TABS.find((t) => t.key === active);

  const downloadTemplate = async () => {
    try {
      const r = await api.get(`/import/templates/${active}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = `template-${active}.xlsx`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Gagal download template");
    }
  };

  const doUpload = async () => {
    if (!file) return toast.error("Pilih file Excel dulu");
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/import/${active}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`${r.data.created} baris berhasil diimport`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal import");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Import Data Excel" subtitle="Import data lama dari file Excel: stok mobil, penjualan, dan pembayaran cicilan" />
      <div className="p-4 md:p-6 lg:p-10">
        <div className="flex gap-0 border border-gray-200 -mb-px">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => { setActive(t.key); setResult(null); setFile(null); }}
              data-testid={`import-tab-${t.key}`}
              className={`px-6 py-4 text-xs uppercase tracking-wider border-r border-gray-200 last:border-r-0 ${active === t.key ? "bg-[#0A0A0A] text-white font-semibold" : "bg-white text-gray-700 hover:bg-gray-50"}`}
            >
              {t.title}
            </button>
          ))}
        </div>
        <div className="border border-gray-200 p-8">
          <h2 className="font-display text-xl font-semibold">{activeCfg.title}</h2>
          <p className="text-sm text-gray-600 mt-2">{activeCfg.desc}</p>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={downloadTemplate}
              data-testid="import-template-btn"
              className="border border-gray-900 p-6 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500 font-medium">
                <DownloadSimple size={12} weight="bold" /> Langkah 1
              </div>
              <div className="font-display text-lg font-semibold mt-2">Download Template Excel</div>
              <div className="text-xs text-gray-500 mt-2">Isi data lama Anda sesuai kolom template</div>
            </button>

            <label
              className="border border-gray-900 p-6 text-left hover:bg-gray-50 transition-colors cursor-pointer block"
              data-testid="import-file-label"
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500 font-medium">
                <UploadSimple size={12} weight="bold" /> Langkah 2
              </div>
              <div className="font-display text-lg font-semibold mt-2">Pilih File Excel</div>
              <div className="text-xs text-gray-500 mt-2 font-mono-num truncate">{file ? file.name : "Klik untuk pilih file .xlsx"}</div>
              <input
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                data-testid="import-file-input"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
              />
            </label>
          </div>

          <Button
            onClick={doUpload}
            disabled={!file || busy}
            data-testid="import-upload-btn"
            className="mt-6 w-full rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-11"
          >
            <UploadSimple size={14} className="mr-2" /> {busy ? "Memproses..." : "Import Sekarang"}
          </Button>

          {result && (
            <div className="mt-6 border border-gray-200 p-5" data-testid="import-result">
              <div className="flex items-center gap-2">
                <CheckCircle size={20} weight="bold" className="text-[#10B981]" />
                <div className="font-semibold">{result.created} baris berhasil diimport</div>
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center gap-2 text-[#E63946]">
                    <WarningCircle size={16} weight="bold" />
                    <div className="text-xs uppercase tracking-wider">{result.errors.length} baris gagal</div>
                  </div>
                  <div className="mt-2 max-h-48 overflow-y-auto text-xs font-mono-num bg-gray-50 p-3">
                    {result.errors.map((e, i) => <div key={i} className="py-1">{e}</div>)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
